/**
 * One human, two backend records, one view model.
 *
 * The identity record holds name and image; the consumer row holds everything
 * else. They are written by different routes under different field names —
 * the same image is `profileImage` on the identity and `profilePicUrl` on the
 * consumer. Screens read `UserProfile` and never touch a raw DTO.
 */

import { joinName, toUsageType } from "../auth/authMappers";

export const EMPTY_PROFILE = {
  id: null,
  identityId: null,
  firstName: "",
  lastName: "",
  fullName: "",
  email: null,
  phoneNumber: null,
  emailVerified: false,
  phoneNumberVerified: false,
  profileImage: null,
  dateOfBirth: null,
  gender: null,
  usage: null,
  address: null,
  addressLabel: null,
  addressCoords: null,
  avgRating: 0,
  totalRatings: 0,
  active: true,
  createdAt: null,
};

/**
 * `address1` arrives comma-stuffed from geocoding, so collapse it before
 * appending city and country.
 */
export const formatAddressLabel = (address) => {
  if (!address) return null;

  const line = String(address.address1 || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .join(", ");

  const parts = [line, address.city, address.countryCode].filter(Boolean);

  return parts.length ? parts.join(", ") : null;
};

/** The server rejects its own `address.id` coming back. */
export const stripAddressId = (address) => {
  if (!address) return null;

  const { id, ...rest } = address;
  return rest;
};

export const toUserProfile = (dto) => {
  if (!dto) return EMPTY_PROFILE;

  const address = dto.address ?? null;

  // Both must be numbers, so the map picker never opens on a half-null coord.
  const hasCoords =
    typeof address?.latitude === "number" &&
    typeof address?.longitude === "number";

  return {
    id: dto.id ?? null,
    identityId: dto.identityId ?? null,
    firstName: dto.firstName ?? "",
    lastName: dto.lastName ?? "",
    fullName: joinName(dto.firstName, dto.lastName),
    email: dto.email ?? null,
    phoneNumber: dto.phoneNumber ?? null,
    emailVerified: dto.emailVerified ?? false,
    phoneNumberVerified: dto.phoneNumberVerified ?? false,
    profileImage: dto.profilePicUrl ?? null,
    dateOfBirth: dto.dateOfBirth ?? null,
    gender: dto.gender ?? null,
    usage: toUsageType(dto.type),
    address,
    addressLabel: formatAddressLabel(address),
    addressCoords: hasCoords
      ? { latitude: address.latitude, longitude: address.longitude }
      : null,
    avgRating: dto.avgRating ?? 0,
    totalRatings: dto.totalRatings ?? 0,
    active: dto.active ?? true,
    createdAt: dto.createdAt ?? null,
  };
};

/** A cached profile may predate the view model, so never trust its shape. */
export const normalizeStoredUser = (value) => {
  if (!value || typeof value !== "object") return EMPTY_PROFILE;
  if (typeof value.fullName === "string") return { ...EMPTY_PROFILE, ...value };

  return toUserProfile(value);
};

/**
 * Overlays the identity record onto the consumer row with `??`, so a null
 * from one never wipes a value from the other. `||` would drop a legitimately
 * empty string and plain assignment would clobber.
 *
 * `/api/users/me/` is inconsistent about casing, so every spelling is read.
 */
export const mergeIdentity = (profile, identity) => {
  if (!identity) return profile;

  const firstName = identity.firstName ?? identity.first_name;
  const lastName = identity.lastName ?? identity.last_name;
  const email = identity.email;
  const phoneNumber = identity.phoneNumber ?? identity.phone_number;
  const profileImage =
    identity.profileImage ?? identity.profile_image ?? identity.profilePicUrl;

  const merged = {
    ...profile,
    identityId: profile.identityId ?? identity.id ?? null,
    firstName: firstName ?? profile.firstName,
    lastName: lastName ?? profile.lastName,
    email: email ?? profile.email,
    phoneNumber: phoneNumber ?? profile.phoneNumber,
    profileImage: profileImage ?? profile.profileImage,
  };

  return { ...merged, fullName: joinName(merged.firstName, merged.lastName) };
};

/**
 * Diffs against the cached profile and returns null when nothing the identity
 * record cares about changed — that is what stops every save firing a
 * pointless PATCH.
 */
export const toIdentityPayload = (profile, patch) => {
  const payload = {};

  if (patch.firstName !== undefined && patch.firstName !== profile.firstName) {
    payload.firstName = patch.firstName;
  }

  if (patch.lastName !== undefined && patch.lastName !== profile.lastName) {
    payload.lastName = patch.lastName;
  }

  if (
    patch.profileImage !== undefined &&
    patch.profileImage !== profile.profileImage
  ) {
    payload.profileImage = patch.profileImage;
  }

  return Object.keys(payload).length ? payload : null;
};

/**
 * The opposite of toIdentityPayload: merges the patch over the full cached
 * profile and emits EVERYTHING, because `/consumers/save/` is a full replace
 * that blanks any field you omit.
 *
 * `phoneNumber` and `email` fall back to "" rather than being dropped — the
 * endpoint treats a missing key as "blank this".
 */
export const toSavePayload = (profile, patch = {}) => {
  const next = { ...profile, ...patch };
  const address = stripAddressId(next.address);

  return {
    firstName: next.firstName,
    lastName: next.lastName,
    phoneNumber: next.phoneNumber ?? "",
    email: next.email ?? "",
    dateOfBirth: next.dateOfBirth,
    gender: next.gender,
    ...(next.usage ? { type: next.usage } : {}),
    ...(next.profileImage ? { profilePicUrl: next.profileImage } : {}),
    ...(address ? { address } : {}),
  };
};

/** Builds the first save from the signup draft. */
export const draftToProfile = (draft) => ({
  ...EMPTY_PROFILE,
  firstName: draft?.firstName ?? "",
  lastName: draft?.lastName ?? "",
  fullName: joinName(draft?.firstName, draft?.lastName),
  email: draft?.email ?? null,
  phoneNumber: draft?.phoneNumber ?? null,
  dateOfBirth: draft?.dateOfBirth ?? null,
  gender: draft?.gender ?? null,
  usage: draft?.usageType ?? null,
});
